/* eslint-disable no-nested-ternary, react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  Alert,
  Button,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { Iconify } from 'src/components';
import WhatsAppTemplatePreviewPanel from 'src/components/whatsappComponents/WhatsAppTemplatePreviewPanel';
import SandboxedHtmlPreview from 'src/components/dialogPopUp/SendFormsDialog/SandboxedHtmlPreview';
import { callApi } from 'src/utils/apiCall';
import { APPLE, appleInputSx } from './appleDesignTokens';

const DRAWER_WIDTH = 480;
const EDITOR_MODE_STORAGE_KEY = 'wa-template-editor-mode';

// Merge-tag catalogue for Basic mode. Users pick one of these per variable
// position instead of typing a raw string and wondering what goes there.
// Keep the list tight — in Basic mode the point is "obvious choices, not
// every possible field". Power users get full freedom in Advanced mode.
const MERGE_TAG_CATALOGUE = [
  { group: 'Patient', label: 'First name', value: '{lead.first_name}' },
  { group: 'Patient', label: 'Last name', value: '{lead.last_name}' },
  { group: 'Patient', label: 'Full name', value: '{lead.full_name}' },
  { group: 'Patient', label: 'Phone', value: '{lead.phone}' },
  { group: 'Patient', label: 'Email', value: '{lead.email}' },
  { group: 'Treatment', label: 'Treatment name', value: '{treatment.name}' },
  { group: 'Treatment', label: 'Treatment price', value: '{treatment.price}' },
  { group: 'Appointment', label: 'Appointment date', value: '{appointment.date}' },
  { group: 'Appointment', label: 'Appointment time', value: '{appointment.time}' },
  { group: 'Appointment', label: 'Practitioner name', value: '{appointment.practitioner}' },
  { group: 'Practice', label: 'Practice name', value: '{practice.name}' },
  { group: 'Practice', label: 'Practice phone', value: '{practice.phone}' },
];

const TemplatePreviewDrawer = ({
  open,
  onClose,
  template = null,
  type,
  onUpdateContextMap = null,
  onEdit = null,
  practiceId = null,
}) => {
  const [tagData, setTagData] = useState({});
  const headerTagRef = useRef(null);
  const bodyTagRef = useRef(null);
  const footerTagRef = useRef(null);

  // Preview-from-BE state. When ``practiceId`` is supplied (the
  // Engagement Messages tab passes it from EmailTemplateDataGrid),
  // we fetch the rendered subject + html from the PR-2 preview
  // endpoint so the drawer shows what the recipient will see —
  // variables substituted, real Mailgun-template render when the
  // template uses one. The legacy ``template.unlayer_html ||
  // template.body`` path stays as a fallback for callers that don't
  // pass ``practiceId`` and for the offline / fetch-failed branch.
  const [previewData, setPreviewData] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open || !template?.id || !practiceId || type !== 'email') {
      setPreviewData(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    callApi(
      `/api/v1/practices/${practiceId}/engagement-templates/${template.id}/preview/`,
    )
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 200 && res.data) {
          setPreviewData(res.data);
        } else {
          setPreviewData(null);
        }
      })
      .catch(() => {
        if (!cancelled) setPreviewData(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, template?.id, practiceId, type]);

  // Basic vs Advanced editor mode. Persists across sessions per-user so a
  // user who prefers Advanced doesn't get kicked back to Basic every visit.
  const [editorMode, setEditorMode] = useState(() => {
    try {
      return window.localStorage.getItem(EDITOR_MODE_STORAGE_KEY) || 'basic';
    } catch (e) {
      return 'basic';
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem(EDITOR_MODE_STORAGE_KEY, editorMode); } catch (e) { /* non-fatal */ }
  }, [editorMode]);

  // Per-section pending merge-tag selection in Basic mode. Keyed by section
  // (HEADER/BODY/FOOTER), value is the selected catalogue entry's `value`.
  const [pendingTag, setPendingTag] = useState({ HEADER: '', BODY: '', FOOTER: '' });

  React.useEffect(() => {
    if (template?.template_context_mapping) {
      setTagData(template.template_context_mapping);
    } else {
      setTagData({});
    }
  }, [template?.id]);

  const handleAddTag = (section, ref) => {
    const value = ref?.current?.value?.trim();
    if (!value) return;
    ref.current.value = '';
    const updated = { ...tagData, [section]: [...(tagData[section] || []), value] };
    setTagData(updated);
    if (onUpdateContextMap) onUpdateContextMap(updated, template?.id);
  };

  const handleDeleteTag = (section, index) => {
    const arr = [...(tagData[section] || [])];
    arr.splice(index, 1);
    const updated = { ...tagData, [section]: arr };
    setTagData(updated);
    if (onUpdateContextMap) onUpdateContextMap(updated, template?.id);
  };

  // Basic mode: add the currently-picked merge tag to the section.
  const handleAddPickedTag = (section) => {
    const value = pendingTag[section];
    if (!value) return;
    const updated = { ...tagData, [section]: [...(tagData[section] || []), value] };
    setTagData(updated);
    if (onUpdateContextMap) onUpdateContextMap(updated, template?.id);
    setPendingTag((prev) => ({ ...prev, [section]: '' }));
  };

  // Human-readable label for a stored chip. In Basic mode users pick from the
  // catalogue, so stored values match catalogue entries. Free-text values from
  // Advanced mode fall through to the raw string.
  const labelForChip = (chipValue) => {
    const match = MERGE_TAG_CATALOGUE.find((t) => t.value === chipValue);
    return match ? `${match.group}: ${match.label}` : chipValue;
  };

  const tagColors = {
    HEADER: APPLE.green,
    BODY: APPLE.orange,
    FOOTER: APPLE.blue,
  };

  const refs = { HEADER: headerTagRef, BODY: bodyTagRef, FOOTER: footerTagRef };
  const icons = {
    HEADER: 'mdi:page-layout-header',
    BODY: 'mdi:page-layout-body',
    FOOTER: 'mdi:page-layout-footer',
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          p: 0,
          borderRadius: '16px 0 0 16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          fontFamily: APPLE.font,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${APPLE.grayBorder}`,
          bgcolor: APPLE.grayBg,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: APPLE.font }}>
          {type === 'whatsapp' ? 'Template Preview' : 'Email Preview'}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {onEdit && (
            <IconButton
              size="small"
              onClick={() => onEdit(template)}
              sx={{
                bgcolor: 'rgba(0,0,0,0.05)',
                borderRadius: '50%',
                width: 30,
                height: 30,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
              }}
            >
              <Iconify icon="mdi:pencil-outline" width={16} />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              bgcolor: 'rgba(0,0,0,0.05)',
              borderRadius: '50%',
              width: 30,
              height: 30,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
            }}
          >
            <Iconify icon="eva:close-fill" width={18} />
          </IconButton>
        </Stack>
      </Stack>

      <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
        {type === 'whatsapp' && template && (
          <>
            <WhatsAppTemplatePreviewPanel template={template} showVariables contextMapping={Object.keys(tagData).length > 0 ? tagData : (template?.template_context_mapping || null)} />

            {template?.category && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={template.category}
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.72rem',
                    borderRadius: APPLE.segmentedRadius,
                    bgcolor: template.category === 'MARKETING' ? 'secondary.lighter' : 'warning.lighter',
                    color: template.category === 'MARKETING' ? 'secondary.darker' : 'warning.darker',
                  }}
                />
                {template?.status && (
                  <Chip
                    size="small"
                    label={template.status}
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.72rem',
                      borderRadius: APPLE.segmentedRadius,
                      bgcolor: template.status === 'APPROVED' ? 'success.lighter' : template.status === 'REJECTED' ? 'error.lighter' : 'warning.lighter',
                      color: template.status === 'APPROVED' ? 'success.darker' : template.status === 'REJECTED' ? 'error.darker' : 'warning.darker',
                    }}
                  />
                )}
                {template?.language && (
                  <Chip
                    size="small"
                    label={template.language}
                    variant="outlined"
                    sx={{ fontWeight: 500, fontSize: '0.72rem', borderRadius: APPLE.segmentedRadius }}
                  />
                )}
              </Box>
            )}

            {template?.rejected_reason && (
              <Alert severity="error" sx={{ mt: 1.5, borderRadius: '10px', fontSize: '0.78rem' }}>
                {template.rejected_reason}
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Basic / Advanced mode toggle. Basic hides free-text and presents
                a dropdown of known merge tags ("Patient first name" etc) so
                users never wonder what "Variable 1" means. Advanced keeps the
                original free-text workflow for power users. */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: APPLE.font }}>
                Context Tag Mapping
              </Typography>
              <Box sx={{
                display: 'inline-flex',
                bgcolor: APPLE.grayBg,
                borderRadius: '10px',
                p: '2px',
              }}>
                {['basic', 'advanced'].map((m) => (
                  <Button
                    key={m}
                    disableRipple
                    onClick={() => setEditorMode(m)}
                    sx={{
                      textTransform: 'capitalize',
                      fontFamily: APPLE.font,
                      fontWeight: editorMode === m ? 600 : 400,
                      fontSize: '0.72rem',
                      letterSpacing: '-0.01em',
                      px: 1.25,
                      py: 0.25,
                      minWidth: 0,
                      borderRadius: '8px',
                      color: editorMode === m ? 'text.primary' : APPLE.grayText,
                      bgcolor: editorMode === m ? 'background.paper' : 'transparent',
                      boxShadow: editorMode === m ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                      '&:hover': { bgcolor: editorMode === m ? 'background.paper' : 'rgba(0,0,0,0.04)' },
                    }}
                  >
                    {m}
                  </Button>
                ))}
              </Box>
            </Stack>

            {editorMode === 'basic' && (
              <Box sx={{ mb: 1.5, p: 1.25, bgcolor: `${APPLE.blue}10`, borderRadius: '8px', border: `1px solid ${APPLE.blue}30` }}>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontFamily: APPLE.font }}>
                  Pick what each variable represents (e.g. <em>First name</em>, <em>Treatment</em>). The preview above updates live.
                </Typography>
              </Box>
            )}

            {['HEADER', 'BODY', 'FOOTER'].map((section) => (
              <Box key={section} sx={{ mb: 2 }}>
                {editorMode === 'basic' ? (
                  <Stack direction="row" spacing={0.75} sx={{ mb: 0.75 }}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={pendingTag[section]}
                      onChange={(e) => setPendingTag((prev) => ({ ...prev, [section]: e.target.value }))}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (val) => {
                          if (!val) {
                            return (
                              <Typography component="span" sx={{ fontSize: '0.8rem', color: APPLE.grayText, fontFamily: APPLE.font }}>
                                {section.charAt(0) + section.slice(1).toLowerCase()} — pick a variable
                              </Typography>
                            );
                          }
                          const match = MERGE_TAG_CATALOGUE.find((t) => t.value === val);
                          return match ? `${match.group}: ${match.label}` : val;
                        },
                        MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
                      }}
                      sx={{ ...appleInputSx }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Iconify icon={icons[section]} sx={{ color: 'text.disabled' }} />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {MERGE_TAG_CATALOGUE.map((tag) => (
                        <MenuItem key={tag.value} value={tag.value} sx={{ fontSize: '0.82rem' }}>
                          <Box>
                            <Typography component="span" sx={{ fontSize: '0.7rem', color: 'text.disabled', mr: 1 }}>
                              {tag.group}
                            </Typography>
                            {tag.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                    <Tooltip title="Add this variable to this section">
                      <span>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleAddPickedTag(section)}
                          disabled={!pendingTag[section]}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', borderRadius: '10px', minWidth: 68 }}
                        >
                          Add
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : (
                  <TextField
                    inputRef={refs[section]}
                    size="small"
                    fullWidth
                    placeholder={`${section.charAt(0) + section.slice(1).toLowerCase()} Tags (free text)`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddTag(section, refs[section]);
                    }}
                    sx={{ mb: 0.75, ...appleInputSx }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon={icons[section]} sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(tagData[section] || []).map((chip, idx) => (
                    <Chip
                      key={idx}
                      label={labelForChip(chip)}
                      size="small"
                      onDelete={() => handleDeleteTag(section, idx)}
                      sx={{
                        bgcolor: `${tagColors[section]}18`,
                        color: tagColors[section],
                        fontWeight: 500,
                        fontSize: '0.72rem',
                        fontFamily: APPLE.font,
                        borderRadius: APPLE.segmentedRadius,
                        '& .MuiChip-deleteIcon': { color: tagColors[section], opacity: 0.6, '&:hover': { opacity: 1 } },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </>
        )}

        {type === 'email' && template && (
          <Box>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Subject:</strong>{' '}
                {previewData?.subject || template.subject || '--'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Channel:</strong> {template.channel || '--'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Category:</strong> {template.category || '--'}
              </Typography>
              {previewData?.is_mailgun_template && (
                <Chip
                  label="Mailgun template"
                  size="small"
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start', height: 22, fontSize: '11px' }}
                />
              )}
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {previewLoading ? (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.disabled' }}>
                <Typography variant="caption">Loading preview…</Typography>
              </Box>
            ) : (
              <SandboxedHtmlPreview
                html={
                  previewData?.html
                  || template.unlayer_html
                  || template.body
                  || ''
                }
                height={520}
                ariaLabel="Email body preview"
              />
            )}
            {previewData
              && Array.isArray(previewData.unresolved_variables)
              && previewData.unresolved_variables.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700, color: 'text.secondary',
                    textTransform: 'uppercase', fontSize: '10.5px',
                    letterSpacing: '0.04em', mr: 0.5, alignSelf: 'center',
                  }}
                >
                  Unresolved:
                </Typography>
                {previewData.unresolved_variables.slice(0, 8).map((name) => (
                  <Tooltip
                    key={name}
                    title={`This template references $${name} but the system doesn't have a value for it — it will appear blank when sent.`}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`$${name}`}
                      sx={{ height: 22, fontSize: '11px' }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {!template && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: 'text.disabled' }}>
            <Typography>Select a template to preview</Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

TemplatePreviewDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  template: PropTypes.object,
  type: PropTypes.oneOf(['whatsapp', 'email']).isRequired,
  onUpdateContextMap: PropTypes.func,
  onEdit: PropTypes.func,
  // When supplied, the drawer fetches a real BE-rendered preview
  // (PR-2 endpoint) so variables are substituted and the body
  // reflects what the recipient will actually receive. Without it
  // the drawer falls back to the raw template body (legacy
  // behaviour) — and "No content" can show up when the list
  // endpoint trims the body field, which is exactly the bug
  // Subhadeep hit on 2026-05-27.
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};


export default TemplatePreviewDrawer;
